import { Application } from "@pixi/react";
import { StarPixi, StarSize, useQuestStarHandlers, useStarPixiAssets } from "@shared";
import { QuestStar, useMainQuest, useUserBankTaps } from "@store";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import styles from './QuestStarsPixi.module.scss';

export const QuestStarsPixi = memo(function QuestStarsPixi(props: {
  visibleStars?: QuestStar[],
  persents?: boolean,
  size?: StarSize,
  notFullScreen?: boolean,
  anchor?: number
  onClick?: (id: number) => void
}) {
  const [starCanvasEl, setStarCanvasEl] = useState<HTMLDivElement | null>(null)
  const [starCanvasSize, setStarCanvasSize] = useState<{ width: number, height: number } | null>(null)
  const availableMainStars = useMainQuest.store.availableStars()
  const mainTaps = useUserBankTaps()
  const { handleClick, handleDisappear } = useQuestStarHandlers()
  const { textures: starTextures, isLoaded: starAssetsLoaded } =
    useStarPixiAssets()

  const setStarCanvasRef = useCallback((node: HTMLDivElement | null) => {
    setStarCanvasEl(node)
  }, [])

  useEffect(() => {
    if (!starCanvasEl) return
    const starCanvas = starCanvasEl
    if (!starCanvas) return

    const updateSize = () => {
      const rect = starCanvas.getBoundingClientRect()
      setStarCanvasSize({
        width: rect.width,
        height: rect.height,
      })
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        setStarCanvasSize({ width, height })
      }
    })

    resizeObserver.observe(starCanvas)
    const timeoutId = setTimeout(updateSize, 0)
    window.addEventListener('resize', updateSize)


    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateSize)
      clearTimeout(timeoutId)
    }
  }, [starCanvasEl])

  const sizeInfo = useMemo(() => {
    if (!starCanvasSize || !starCanvasEl) return null;

    if (props.notFullScreen) {
      return {
        ratio: window.devicePixelRatio || 2,
        offsetX: 0,
        offsetY: 0,
        width: starCanvasSize.width,
        height: starCanvasSize.height,
        fullHeight: starCanvasSize.height,
        fullWidth: starCanvasSize.width
      }
    }

    if (starCanvasEl.style.position !== 'absolute') {
      starCanvasEl.style.position = 'absolute';
      starCanvasEl.style.left = -1 * starCanvasEl.getBoundingClientRect().left + 'px';
      starCanvasEl.style.top = -1 * starCanvasEl.getBoundingClientRect().top + 'px';
    }
    const offsetX = -1 * parseInt(starCanvasEl.style.left);
    const offsetY = -1 * parseInt(starCanvasEl.style.top);

    return {
      ratio: window.devicePixelRatio || 2,
      offsetX: offsetX,
      offsetY: offsetY,
      width: starCanvasSize.width,
      height: starCanvasSize.height,
      fullWidth: starCanvasSize.width + offsetX * 2,
      fullHeight: starCanvasSize.height + offsetY * 2
    }
  }, [starCanvasSize, starCanvasEl])

  if (!availableMainStars.length) return null;

  return <div ref={setStarCanvasRef} className={styles.starsLayout}>
    {sizeInfo && <Application
      width={sizeInfo.fullWidth}
      height={sizeInfo.fullHeight}
      backgroundAlpha={0}
      roundPixels
      autoDensity
      antialias={false}
      eventMode="static"
      resolution={sizeInfo.ratio}
      onInit={(app) => {
        app.stage.hitArea = app.screen
      }}
    >
      {starAssetsLoaded && availableMainStars.map((star) => {
        if (props.visibleStars && !props.visibleStars.find(s => s.id === star.id)) return;
        return (
          <StarPixi
            key={star.id}
            anchor={props.anchor}
            textures={starTextures}
            size={props.size || star.size}
            x={sizeInfo.offsetX + (props.persents ? sizeInfo.width * star.x : sizeInfo.width * star.x / 100)}
            y={sizeInfo.offsetY + (props.persents ? sizeInfo.height * star.y : sizeInfo.height * star.y / 100)}
            onClick={() => {
              handleClick(star.id)
              props.onClick?.(star.id)
            }}
            shouldAnimate
            disabled={mainTaps?.balance === 0}
            onDisappear={() =>
              handleDisappear(star.id)
            }
          />
        )
      })}
    </Application>}
  </div>
})